// src/pages/dashboard/Payment_Received.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "./payment_received.css";

const Payment_Received = () => {
  const navigate = useNavigate();

  // ──────────────────────────────────────────────────────────────
  // 1. Customers (fetched from backend – same endpoint as AddInvoice)
  // ──────────────────────────────────────────────────────────────
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedCustomerPAN, setSelectedCustomerPAN] = useState("");

  // ──────────────────────────────────────────────────────────────
  // 2. Form fields
  // ──────────────────────────────────────────────────────────────
  const [amountReceived, setAmountReceived] = useState("");
  const [bankCharges, setBankCharges] = useState("");
  const [paymentDate] = useState(new Date().toISOString().split("T")[0]);
  const [paymentNo, setPaymentNo] = useState("");
  const [paymentMode, setPaymentMode] = useState("Cash");
  const [depositTo, setDepositTo] = useState("Petty Cash");
  const [reference, setReference] = useState("");
  const [taxDeducted, setTaxDeducted] = useState("no"); // "no" | "yes"
  const [notes, setNotes] = useState("");
  const [attachments, setAttachments] = useState([]);

  // ──────────────────────────────────────────────────────────────
  // 3. Invoices table (filtered by selected customer)
  // ──────────────────────────────────────────────────────────────
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState({}); // { invoiceId: paymentAmount }

  // ──────────────────────────────────────────────────────────────
  // Load customers
  // ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const loadCustomers = async () => {
      const userId = localStorage.getItem("userId");
      if (!userId) return;

      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/customers/get?userId=${userId}`,
          { credentials: "include" }
        );
        const data = await res.json();
        if (data.success && Array.isArray(data.data)) {
          const sorted = data.data.sort((a, b) => {
            const nameA = getDisplayName(a).toLowerCase();
            const nameB = getDisplayName(b).toLowerCase();
            return nameA.localeCompare(nameB);
          });
          setCustomers(sorted);
        }
      } catch (err) {
        console.error("Failed to load customers", err);
      }
    };
    loadCustomers();
  }, []);

  // ──────────────────────────────────────────────────────────────
  // Load unpaid invoices when a customer is selected
  // ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!selectedCustomer) {
      setInvoices([]);
      setSelectedInvoices({});
      return;
    }

    const loadUnpaidInvoices = async () => {
      const userId = localStorage.getItem("userId");
      try {
        const res = await fetch(
          `${
            process.env.REACT_APP_BACKEND_URL
          }/api/invoices/unpaid?userId=${userId}&customer=${encodeURIComponent(
            selectedCustomer
          )}`,
          { credentials: "include" }
        );
        const data = await res.json();
        if (data.success && Array.isArray(data.invoices)) {
          setInvoices(data.invoices);
        }
      } catch (err) {
        console.error("Failed to load invoices", err);
      }
    };
    loadUnpaidInvoices();
  }, [selectedCustomer]);

  // ──────────────────────────────────────────────────────────────
  // Helper: display name (same as AddInvoice)
  // ──────────────────────────────────────────────────────────────
  const getDisplayName = (cust) => {
    const full = [cust.firstName, cust.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    const primary = full || cust.displayName || cust.companyName || "Unnamed";
    const secondary =
      cust.companyName && cust.companyName !== primary
        ? ` (${cust.companyName})`
        : "";
    return primary + secondary;
  };

  // ──────────────────────────────────────────────────────────────
  // When customer changes → show PAN
  // ──────────────────────────────────────────────────────────────
  const handleCustomerChange = (e) => {
    const name = e.target.value;
    setSelectedCustomer(name);
    const cust = customers.find((c) => getDisplayName(c) === name);
    setSelectedCustomerPAN(cust?.pan || "");
  };

  // ──────────────────────────────────────────────────────────────
  // Invoice payment input change
  // ──────────────────────────────────────────────────────────────
  const handleInvoicePaymentChange = (invoiceId, value) => {
    const num = Number(value) || 0;
    const invoice = invoices.find((i) => i._id === invoiceId);
    const max = invoice ? invoice.balanceDue : 0;

    setSelectedInvoices((prev) => ({
      ...prev,
      [invoiceId]: num > max ? max : num,
    }));
  };

  // ──────────────────────────────────────────────────────────────
  // Calculations
  // ──────────────────────────────────────────────────────────────
  const totalPaid = useMemo(() => {
    return Object.values(selectedInvoices).reduce((sum, v) => sum + v, 0);
  }, [selectedInvoices]);

  const received = Number(amountReceived) || 0;
  const charges = Number(bankCharges) || 0;
  const netReceived = received - charges;
  const amountUsed = totalPaid;
  const amountRefunded = 0; // could be added later
  const amountInExcess = netReceived - amountUsed;

  // ──────────────────────────────────────────────────────────────
  // Currency symbol (based on first invoice of customer)
  // ──────────────────────────────────────────────────────────────
  const currency = useMemo(() => {
    if (invoices.length === 0) return "INR";
    // Assuming all invoices of a customer have same currency
    return invoices[0].currency || "INR";
  }, [invoices]);

  const formatCurrency = (amt) => {
    return amt.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // ──────────────────────────────────────────────────────────────
  // Save handlers
  // ──────────────────────────────────────────────────────────────
  const handleSave = async (status) => {
    if (!selectedCustomer) return alert("Select a customer");
    if (!amountReceived) return alert("Enter amount received");

    const payload = {
      customer: selectedCustomer,
      pan: selectedCustomerPAN,
      amountReceived: received,
      bankCharges: charges,
      paymentDate,
      paymentNo,
      paymentMode,
      depositTo,
      reference,
      taxDeducted: taxDeducted === "yes",
      notes,
      attachments: attachments.map((f) => f.name),
      payments: Object.entries(selectedInvoices)
        .filter(([, amt]) => amt > 0)
        .map(([invoiceId, amount]) => ({
          invoiceId,
          amount,
        })),
      status, // "DRAFT" or "PAID"
    };

    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/record`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        }
      );
      const data = await res.json();
      if (data.success) {
        navigate("/sales/payments");
      } else {
        alert("Error: " + data.error);
      }
    } catch (err) {
      alert("Network error");
    }
  };

  // ──────────────────────────────────────────────────────────────
  // File attachments
  // ──────────────────────────────────────────────────────────────
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + attachments.length > 5) {
      alert("Maximum 5 files allowed");
      return;
    }
    setAttachments((prev) => [...prev, ...files]);
  };

  const removeAttachment = (idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  // ──────────────────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────────────────
  return (
    <div className="payment-received-page">
      <h1 className="page-title">Record Payment</h1>

      <div className="form-container">
        {/* ───── Customer ───── */}
        <div className="form-row">
          <div className="form-group">
            <label>
              Customer Name <span className="required">*</span>
            </label>
            <select
              value={selectedCustomer}
              onChange={handleCustomerChange}
              className="customer-select"
            >
              <option value="">Select Customer</option>
              {customers.map((c) => (
                <option key={c._id} value={getDisplayName(c)}>
                  {getDisplayName(c)}
                </option>
              ))}
            </select>
            {selectedCustomerPAN && (
              <div className="pan-display">PAN: {selectedCustomerPAN}</div>
            )}
          </div>

          {/* ───── Amount Received ───── */}
          <div className="form-group">
            <label>
              Amount Received <span className="required">*</span>
            </label>
            <div className="currency-input">
              <span className="currency-symbol">{currency}</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={amountReceived}
                onChange={(e) => setAmountReceived(e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>
        </div>

        {/* ───── Bank Charges & Payment Date ───── */}
        <div className="form-row">
          <div className="form-group">
            <label>Bank Charges (if any)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={bankCharges}
              onChange={(e) => setBankCharges(e.target.value)}
              placeholder="0.00"
            />
          </div>
          <div className="form-group">
            <label>Payment Date</label>
            <input type="date" value={paymentDate} readOnly />
          </div>
        </div>

        {/* ───── Payment # & Mode ───── */}
        <div className="form-row">
          <div className="form-group">
            <label>Payment #</label>
            <input
              type="text"
              value={paymentNo}
              onChange={(e) => setPaymentNo(e.target.value)}
              placeholder="e.g. 1"
            />
          </div>
          <div className="form-group">
            <label>Payment Mode</label>
            <select
              value={paymentMode}
              onChange={(e) => setPaymentMode(e.target.value)}
            >
              <option>Cash</option>
              <option>Bank Remittance</option>
              <option>Bank Transfer</option>
              <option>Cheque</option>
              <option>Credit Card</option>
              <option>U L</option>
            </select>
          </div>
        </div>

        {/* ───── Deposit To & Reference ───── */}
        <div className="form-row">
          <div className="form-group">
            <label>Deposit To</label>
            <select
              value={depositTo}
              onChange={(e) => setDepositTo(e.target.value)}
            >
              <option>Petty Cash</option>
              <option>Undeposited Funds</option>
              <option>Employee Reimbursements</option>
              <option>TDS Payable</option>
            </select>
          </div>
          <div className="form-group">
            <label>Reference #</label>
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="e.g. 33"
            />
          </div>
        </div>

        {/* ───── Tax Deducted ───── */}
        <div className="form-row">
          <div className="form-group radio-group">
            <label>Tax deducted?</label>
            <div className="radio-options">
              <label>
                <input
                  type="radio"
                  name="tax"
                  value="no"
                  checked={taxDeducted === "no"}
                  onChange={(e) => setTaxDeducted(e.target.value)}
                />{" "}
                No Tax deducted
              </label>
              <label>
                <input
                  type="radio"
                  name="tax"
                  value="yes"
                  checked={taxDeducted === "yes"}
                  onChange={(e) => setTaxDeducted(e.target.value)}
                />{" "}
                Yes, TDS (Income Tax)
              </label>
            </div>
          </div>
        </div>

        {/* ───── Unpaid Invoices Table ───── */}
        <div className="table-section">
          <div className="table-header">
            <span>Unpaid Invoices</span>
            <button className="filter-btn">Filter by Date Range</button>
            <button className="clear-btn">Clear Applied Amount</button>
          </div>

          {invoices.length === 0 ? (
            <p className="no-invoices">
              There are no unpaid invoices associated with this customer.
            </p>
          ) : (
            <table className="invoices-table">
              <thead>
                <tr>
                  <th>DATE</th>
                  <th>INVOICE NUMBER</th>
                  <th>INVOICE AMOUNT</th>
                  <th>AMOUNT DUE</th>
                  <th>PAYMENT RECEIVED ON</th>
                  <th>PAYMENT</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv._id}>
                    <td>{new Date(inv.invoiceDate).toLocaleDateString()}</td>
                    <td>{inv.invoiceNo}</td>
                    <td>
                      {currency} {formatCurrency(inv.amount)}
                    </td>
                    <td>
                      {currency} {formatCurrency(inv.balanceDue)}
                    </td>
                    <td>
                      <input type="date" disabled />
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={selectedInvoices[inv._id] || ""}
                        onChange={(e) =>
                          handleInvoicePaymentChange(inv._id, e.target.value)
                        }
                        placeholder="0.00"
                        style={{ width: "90px" }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ───── Summary Box (right corner) ───── */}
        <div className="summary-box">
          <div className="summary-row">
            <span>Amount Received :</span>
            <strong>
              {currency} {formatCurrency(received)}
            </strong>
          </div>
          <div className="summary-row">
            <span>Amount used for Payments :</span>
            <strong>
              {currency} {formatCurrency(amountUsed)}
            </strong>
          </div>
          <div className="summary-row">
            <span>Amount Refunded :</span>
            <strong>
              {currency} {formatCurrency(amountRefunded)}
            </strong>
          </div>
          <div
            className={`summary-row ${
              amountInExcess >= 0 ? "excess" : "short"
            }`}
          >
            <span>Amount in Excess :</span>
            <strong>
              {currency} {formatCurrency(amountInExcess)}
            </strong>
          </div>
        </div>

        {/* ───── Notes ───── */}
        <div className="form-row full-width">
          <div className="form-group">
            <label>Notes (Internal use. Not visible to customer)</label>
            <textarea
              rows="3"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add internal notes..."
            />
          </div>
        </div>

        {/* ───── Attachments ───── */}
        <div className="form-row full-width">
          <div className="form-group">
            <label>Attachments</label>
            <div className="upload-area">
              <input type="file" multiple onChange={handleFileChange} />
              <p>Display attachments in Customer Portal and emails</p>
              <p className="small">
                You can upload a maximum of 5 files, 5MB each.
              </p>
            </div>
            <div className="attachment-list">
              {attachments.map((file, idx) => (
                <div key={idx} className="attachment-item">
                  <span>{file.name}</span>
                  <button
                    type="button"
                    className="remove-file"
                    onClick={() => removeAttachment(idx)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ───── Action Buttons ───── */}
        <div className="action-bar">
          <button className="btn draft" onClick={() => handleSave("DRAFT")}>
            Save as Draft
          </button>
          <button className="btn paid" onClick={() => handleSave("PAID")}>
            Save as Paid
          </button>
          <button className="btn cancel" onClick={() => navigate(-1)}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default Payment_Received;
